import type { AtlasManifest } from '@atlas-manifest/types';
import { ObjectHeader } from '../components/ObjectHeader.js';
import { EmptyState } from '../components/EmptyState.js';
import { EntityChip } from '../components/EntityChip.js';
import { Chip } from '../components/Chip.js';
import { Icon } from '../components/Icon.js';
import { EnrichmentFields } from '../components/EnrichmentFields.js';
import { RelationshipsPanel } from '../components/RelationshipsPanel.js';
import { cloudFunctionById } from '../state/selectors.js';
import { useAppStore } from '../state/store.js';
import { navigateToObject, navigate, navigateToObjectKind } from '../state/hashRouter.js';
import { CLOUD_FUNCTION_ATTRIBUTE_DOCS } from '../state/attributeGlossary.js';
import { WorkflowTrail } from './WorkflowTrail.js';
import { PresenceContainer } from './PresenceContainer.js';
import { AnnotationsContainer } from './AnnotationsContainer.js';
import { AskAtlasToEditBox } from '../components/AskAtlasToEditBox.js';
import { streamAskAtlas } from '../firebase/atlasApi.js';

export interface CloudFunctionDetailViewProps {
  manifest: AtlasManifest;
  functionName: string;
}

export function CloudFunctionDetailView({ manifest, functionName }: CloudFunctionDetailViewProps): React.JSX.Element {
  const fn = cloudFunctionById(manifest, functionName);
  if (!fn) {
    return <EmptyState icon="help" title={`Function ${functionName} not found.`} />;
  }

  const { entityTrail } = useAppStore();

  return (
    <div className="detail-view">
      <ObjectHeader
        object={fn}
        icon="bolt"
        iconLabel="Cloud function"
        badges={[{ label: fn.domain, type: 'domain' as const }]}
        title={fn.name}
        docRef={<>{fn.kind}{fn.apiSpecRef ? <> · {fn.apiSpecRef}</> : null}</>}
        description={fn.description}
        tags={fn.tags}
        actions={
          <>
            <button className="btn btn-ghost" onClick={() => navigate({ kind: 'sandbox', seedObject: { kind: 'cloud-function', id: fn.name } })}>
              <Icon name="edit" size="sm" />
              Edit in Sandbox
            </button>
            <button className="btn btn-ghost" onClick={() => navigate({ kind: 'ask', focus: fn.name })}>
              <Icon name="auto_awesome" size="sm" />
              Ask Atlas
            </button>
          </>
        }
      />
      <dl className="sheet-hero-stats" aria-label="stats">
        <div>
          <dt>READS</dt>
          <dd>{fn.reads.length}</dd>
        </div>
        <div>
          <dt>WRITES</dt>
          <dd>{fn.writes.length}</dd>
        </div>
        <div>
          <dt>EMITS</dt>
          <dd>{(fn.emitsEvents ?? []).length}</dd>
        </div>
      </dl>
      <EnrichmentFields
        fields={[
          { key: 'rationale', label: 'Rationale', value: fn.rationale, glossary: CLOUD_FUNCTION_ATTRIBUTE_DOCS['rationale'] },
          { key: 'triggerCondition', label: 'Trigger Condition', value: fn.triggerCondition, glossary: CLOUD_FUNCTION_ATTRIBUTE_DOCS['triggerCondition'] },
          { key: 'slaMs', label: 'SLA (ms)', value: fn.slaMs, glossary: CLOUD_FUNCTION_ATTRIBUTE_DOCS['slaMs'] },
          { key: 'retryPolicy', label: 'Retry Policy', value: fn.retryPolicy, glossary: CLOUD_FUNCTION_ATTRIBUTE_DOCS['retryPolicy'] },
          { key: 'authRequired', label: 'Auth Required', value: fn.authRequired, glossary: CLOUD_FUNCTION_ATTRIBUTE_DOCS['authRequired'] },
        ]}
      />
      {entityTrail.length >= 2 && (
        <WorkflowTrail trail={entityTrail} onJump={navigateToObject} />
      )}
      <PresenceContainer entityId={fn.name} />
      <AskAtlasToEditBox
        kind="cloud-function"
        selectorId={fn.name}
        currentObject={fn as unknown as Record<string, unknown>}
        manifest={manifest}
        onAsk={(opts) => streamAskAtlas(opts.prompt, opts.summary, opts.knownEntityIds)}
      />
      <section style={{ marginBottom: 'var(--sp-lg)' }}>
        <div className="section-label">Reads · {fn.reads.length} entities</div>
        {fn.reads.length > 0 ? (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {fn.reads.map((entityId) => (
              <EntityChip key={entityId} entityId={entityId} manifest={manifest} />
            ))}
          </div>
        ) : (
          <p>None</p>
        )}
      </section>
      <section style={{ marginBottom: 'var(--sp-lg)' }}>
        <div className="section-label">Writes · {fn.writes.length} entities</div>
        {fn.writes.length > 0 ? (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {fn.writes.map((entityId) => (
              <EntityChip key={entityId} entityId={entityId} manifest={manifest} />
            ))}
          </div>
        ) : (
          <p>None</p>
        )}
      </section>
      {fn.emitsEvents && fn.emitsEvents.length > 0 && (
        <section style={{ marginBottom: 'var(--sp-lg)' }}>
          <div className="section-label">Emits events · {fn.emitsEvents.length}</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {fn.emitsEvents.map((eventType) => (
              <Chip
                key={eventType}
                label={eventType}
                onClick={() => navigate({ kind: 'event-type', id: eventType })}
              />
            ))}
          </div>
        </section>
      )}
      {fn.triggeredBy && fn.triggeredBy.length > 0 && (
        <section style={{ marginBottom: 'var(--sp-lg)' }}>
          <div className="section-label">
            Triggered by · {fn.triggeredBy.length}
            {fn.triggeredByInferred && <span style={{ color: 'var(--text3)' }}> (inferred)</span>}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {fn.triggeredBy.map((trigger) => (
              <Chip
                key={`${trigger.kind}-${trigger.id}`}
                label={trigger.id}
                onClick={() =>
                  navigate(
                    trigger.kind === 'surface'
                      ? { kind: 'surface', id: trigger.id }
                      : { kind: 'event-type', id: trigger.id },
                  )
                }
              />
            ))}
          </div>
        </section>
      )}
      <RelationshipsPanel manifest={manifest} kind="cloud-function" id={fn.name} onNavigate={navigateToObjectKind} />
      <AnnotationsContainer entityId={fn.name} />
    </div>
  );
}
